import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Inbox, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import usePageMeta from "@/hooks/usePageMeta";
import { BRAND } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  "export-import-business": "Export-Import Business",
  "consulting-training": "Consulting / Training",
  "ai-digital-services": "AI & Digital",
  other: "Other",
};

export default function Admin() {
  usePageMeta(`Admin — ${BRAND.name}`, "Admin enquiries dashboard.");
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  const [tForm, setTForm] = useState({ quote: "", name: "", role: "" });
  const [editingId, setEditingId] = useState(null);

  const loadTestimonials = async () => {
    try {
      const { data } = await axios.get(`${API}/testimonials`);
      setTestimonials(data);
    } catch {}
  };

  const saveTestimonial = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/testimonials/${editingId}`, tForm, { withCredentials: true });
        toast.success("Testimonial updated.");
      } else {
        await axios.post(`${API}/admin/testimonials`, tForm, { withCredentials: true });
        toast.success("Testimonial added — it's live on the site now.");
      }
      setTForm({ quote: "", name: "", role: "" });
      setEditingId(null);
      loadTestimonials();
    } catch {
      toast.error("Could not save the testimonial.");
    }
  };

  const removeTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { withCredentials: true });
      toast.success("Testimonial deleted.");
      loadTestimonials();
    } catch {
      toast.error("Could not delete the testimonial.");
    }
  };

  const loadEnquiries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        loadEnquiries();
        loadTestimonials();
      })
      .catch(() => setUser(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      setUser(data);
      toast.success("Welcome back.");
      loadEnquiries();
      loadTestimonials();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setEnquiries([]);
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="admin-loading">
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6" data-testid="admin-login-page">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-10"
        >
          <p className="kicker mb-3 text-gold">Admin</p>
          <h1 className="font-serif text-3xl text-white">Enquiries Dashboard</h1>
          <p className="mt-2 text-sm text-slate-500">Sign in to view contact form enquiries.</p>
          <div className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin-email" className="text-slate-300">Email</Label>
              <Input
                id="admin-email"
                type="email"
                required
                data-testid="admin-email-input"
                value={creds.email}
                onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
                className="h-12 border-[#1E3A5F] bg-[#051320] text-slate-200 focus-visible:ring-gold"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-slate-300">Password</Label>
              <Input
                id="admin-password"
                type="password"
                required
                data-testid="admin-password-input"
                value={creds.password}
                onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
                className="h-12 border-[#1E3A5F] bg-[#051320] text-slate-200 focus-visible:ring-gold"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen max-w-6xl px-6 pb-24 pt-36" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="kicker mb-2 text-gold">Admin</p>
          <h1 className="font-serif text-4xl text-white">Enquiries</h1>
          <p className="mt-2 text-sm text-slate-500">Signed in as {user.email}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadEnquiries}
            data-testid="admin-refresh-button"
            className="btn-secondary !px-5 !py-2.5 !text-xs"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> Refresh
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="btn-secondary !px-5 !py-2.5 !text-xs !border-red-400/40 hover:!bg-red-400/10"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign Out
          </button>
        </div>
      </div>

      <div className="mt-10 space-y-4">
        {enquiries.length === 0 && !loading && (
          <div className="flex flex-col items-center rounded-2xl border border-[#1E3A5F] bg-[#0A2540] py-20 text-center" data-testid="admin-empty-state">
            <Inbox className="h-10 w-10 text-slate-600" strokeWidth={1.5} />
            <p className="mt-4 text-sm text-slate-500">No enquiries yet. They'll appear here when the contact form is used.</p>
          </div>
        )}
        {enquiries.map((q) => (
          <div
            key={q.id}
            data-testid={`admin-enquiry-${q.id}`}
            className="rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-7"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-serif text-xl text-white">{q.name}</p>
                <p className="mt-1 text-sm text-slate-400">
                  {q.email} {q.phone ? `· ${q.phone}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-widest text-gold">
                  {SERVICE_LABELS[q.service] || q.service}
                </span>
                <span className="text-xs text-slate-500">
                  {q.created_at ? new Date(q.created_at).toLocaleString() : ""}
                </span>
              </div>
            </div>
            <p className="mt-4 border-t border-[#1E3A5F] pt-4 text-sm leading-relaxed text-slate-300">{q.message}</p>
          </div>
        ))}
      </div>

      {/* TESTIMONIAL MANAGER */}
      <div className="mt-20" data-testid="testimonial-manager">
        <p className="kicker mb-2 text-gold">Testimonials</p>
        <h2 className="font-serif text-3xl text-white">Manage Client Quotes</h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          Add a new client's name and company anytime — it appears on the Testimonials page instantly.
          Until you add your own, the site keeps showing the sample quotes.
        </p>

        <form
          onSubmit={saveTestimonial}
          data-testid="testimonial-form"
          className="mt-6 space-y-4 rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-7"
        >
          <Textarea
            required
            rows={3}
            data-testid="testimonial-quote-input"
            placeholder="Client quote — what did they say about working with you?"
            value={tForm.quote}
            onChange={(e) => setTForm((f) => ({ ...f, quote: e.target.value }))}
            className="border-[#1E3A5F] bg-[#051320] text-slate-200 placeholder:text-slate-600 focus-visible:ring-gold"
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              required
              data-testid="testimonial-name-input"
              placeholder="Client name (e.g. Rajesh Shah)"
              value={tForm.name}
              onChange={(e) => setTForm((f) => ({ ...f, name: e.target.value }))}
              className="h-12 border-[#1E3A5F] bg-[#051320] text-slate-200 placeholder:text-slate-600 focus-visible:ring-gold"
            />
            <Input
              required
              data-testid="testimonial-role-input"
              placeholder="Company / designation (e.g. Founder, ABC Textiles)"
              value={tForm.role}
              onChange={(e) => setTForm((f) => ({ ...f, role: e.target.value }))}
              className="h-12 border-[#1E3A5F] bg-[#051320] text-slate-200 placeholder:text-slate-600 focus-visible:ring-gold"
            />
          </div>
          <div className="flex gap-3">
            <button type="submit" data-testid="testimonial-save-button" className="btn-primary !py-3">
              {editingId ? "Update Testimonial" : "Add Testimonial"}
            </button>
            {editingId && (
              <button
                type="button"
                data-testid="testimonial-cancel-button"
                onClick={() => {
                  setEditingId(null);
                  setTForm({ quote: "", name: "", role: "" });
                }}
                className="btn-secondary !py-3"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        <div className="mt-6 space-y-4">
          {testimonials.length === 0 && (
            <p data-testid="testimonial-empty-note" className="text-sm text-slate-500">
              No testimonials added yet — the site is showing the sample quotes.
            </p>
          )}
          {testimonials.map((t) => (
            <div
              key={t.id}
              data-testid={`admin-testimonial-${t.id}`}
              className="flex items-start justify-between gap-4 rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-6"
            >
              <div>
                <p className="font-serif italic leading-relaxed text-slate-200">"{t.quote}"</p>
                <p className="mt-3 text-sm font-semibold text-white">
                  {t.name} <span className="font-normal text-slate-500">· {t.role}</span>
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  onClick={() => {
                    setEditingId(t.id);
                    setTForm({ quote: t.quote, name: t.name, role: t.role });
                    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
                  }}
                  data-testid={`testimonial-edit-${t.id}`}
                  aria-label="Edit testimonial"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1E3A5F] text-slate-400 transition-colors hover:border-gold hover:text-gold"
                >
                  <Pencil className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => removeTestimonial(t.id)}
                  data-testid={`testimonial-delete-${t.id}`}
                  aria-label="Delete testimonial"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1E3A5F] text-slate-400 transition-colors hover:border-red-400 hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
